import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Columns, Rows } from 'lucide-react';
import { SplitPattern, horizontalPatterns, verticalPatterns } from './types';

interface SplitPatternPickerProps {
  direction: 'horizontal' | 'vertical';
  onSelect: (pattern: SplitPattern) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}

export const SplitPatternPicker: React.FC<SplitPatternPickerProps> = ({
  direction,
  onSelect,
  disabled = false,
  children,
}) => {
  const patterns = direction === 'horizontal' ? horizontalPatterns : verticalPatterns;
  const Icon = direction === 'horizontal' ? Columns : Rows;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {children || (
          <Button variant="ghost" size="sm" disabled={disabled} className="h-7 px-2">
            <Icon className="h-3.5 w-3.5" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <p className="text-xs font-medium text-muted-foreground mb-2 px-1">
          {direction === 'horizontal' ? 'Split into columns' : 'Split into rows'}
        </p>
        <div className="space-y-1">
          {patterns.map((pattern) => (
            <button
              key={pattern.id}
              onClick={() => onSelect(pattern)}
              className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors"
            >
              {/* Pattern preview */}
              <div 
                className={`flex gap-0.5 flex-shrink-0 ${
                  direction === 'horizontal' ? 'w-12 h-6 flex-row' : 'w-6 h-12 flex-col'
                }`}
              >
                {pattern.preview.map((size, i) => {
                  const totalSize = pattern.preview.reduce((a, b) => a + b, 0);
                  const percentage = (size / totalSize) * 100;
                  return (
                    <div
                      key={i}
                      className="bg-primary/20 border border-primary/40 rounded-sm"
                      style={
                        direction === 'horizontal'
                          ? { width: `${percentage}%`, height: '100%' }
                          : { height: `${percentage}%`, width: '100%' }
                      }
                    />
                  );
                })}
              </div>
              <span className="text-sm">{pattern.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
